import type { SlateContent } from 'domains/reporter/Reporter/types';
import type {
  GenerateImpressionMutation,
  GenerateImpressionMutationVariables,
} from '../generated/graphql';
import type { Dispatch, SetStateAction } from 'types';
import { GENERATE_IMPRESSION } from '../modules/Apollo/queries';
import { useMutation } from '@apollo/client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useHeadingKeywords } from './useHeadingKeywords';
import { Editor, ReactEditor, Transforms, Range, Node } from 'domains/reporter/RichTextEditor/core';
import { logger } from 'modules/logger';
import type { Descendant } from 'domains/reporter/RichTextEditor/core';
import { HeadingLevel } from 'domains/reporter/RichTextEditor/plugins/heading/constants';
import {
  insertHeading,
  getHeadingNodeWithSynonym,
  getHeadingSectionEdgesByNode,
} from 'domains/reporter/RichTextEditor/plugins/heading/utils/normalization';
import {
  createInlineParagraph,
  createParagraph,
} from 'domains/reporter/RichTextEditor/plugins/paragraph/utils';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import { convertUnifiedToFields } from 'domains/reporter/Reporter/Fields/utils';
import { partialEditor, slateContentToString } from 'domains/reporter/RichTextEditor/utils';
import { useRecoilState } from 'recoil';
import { prevGeneratedImpressionState } from 'domains/reporter/Reporter/state';
import { useToasterDispatch } from '../common/ui/Toaster/Toaster';
import { useReportLockedState } from 'domains/reporter/Reporter/hooks/useReportLockedState';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';
import { prevReportSectionsState } from '../domains/reporter/Reporter/state';
import { useSlateSingletonContext } from '../domains/reporter/Reporter/SlateSingletonContext';
import equals from 'lodash/fp/equals';
import { slateSource } from 'domains/reporter/RichTextEditor/core/slateSource';
import { annotateTextWithSource } from 'domains/reporter/RichTextEditor/plugins/textSourceStyling/utils';
import { Element, Text } from 'slate';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';

export type ImpressionContent = {
  content: SlateContent | null | undefined;
  range: Range | null | undefined;
  rangeWithHeading: Range | null | undefined;
};

export type GenerateImpressionState = 'initial' | 'loading' | 'visited' | 'stale' | 'error';

export type UseImpressionGeneratorReturn = {
  /**
   * Determines whether the user's selection is entirely within the impression section.
   * @param {Editor} editor - The Slate editor instance.
   * @param {?RangeType} selection - The selection to check. If null, the current selection is used.
   * @returns {boolean} True if selection entirely within the impression section.
   */
  isSelectionInImpressionSection: (editor: Editor, selection?: Range) => boolean;
  /**
   * Retrieves the content and range of the impression section in the editor.
   * @param {Editor} editor - The Slate editor instance.
   * @returns {?ImpressionContent} The content and range of the impression section, or null if not found.
   */
  getImpressionContent: (editor: Editor) => ImpressionContent | null | undefined;
  /**
   * Retrieves the content of all sections of the report except the impression section.
   * @param {Editor} editor - The Slate editor instance.
   * @returns {SlateContent} The content excluding the impression section.
   */
  getNonImpressionContent: (editor: Editor) => SlateContent;
  /**
   * Generates an impression for the current case and returns the content of the impression.
   * @param {Editor} editor - The Slate editor instance.
   * @returns {Promise<?SlateContent>} The generated impression content, or null if generation fails.
   */
  generateImpression: (editor: Editor) => Promise<SlateContent | null | undefined>;
  /**
   * Stitches the given impression content into the editor. If there is no existing impression section,
   * it first creates an impression heading at the end of the report. If there is one, then the existing content
   * is removed and replaced with the new content. Returns the impression and its range.
   * @param {Editor} editor - The Slate editor instance.
   * @param {SlateContent} content - The impression content to be stitched into the editor.
   * @param {SlateContent} isAutoGenerated - Whether the impression is auto-generated. Defaults to false.
   * @returns {?ImpressionContent} The stitched impression content and its range, or null if stitching fails.
   */
  stitchImpression: (
    editor: Editor,
    content: SlateContent,
    isAutoGenerated?: boolean
  ) => ImpressionContent | null | undefined;
  /**
   * Generates an impression and stitches it into the editor. Returns the impression and its range.
   * @param {Editor} editor - The Slate editor instance.
   * @returns {Promise<?ImpressionContent>} The stitched impression content and its range, or null if generation or stitching fails.
   */
  generateAndStitchImpression: (
    editor: Editor,
    isAutoGenerated?: boolean
  ) => Promise<ImpressionContent | null | undefined>;
  /**
   * Determines if the impression in the report is generated by comparing it to the most recently generated impression.
   * @param {Editor} editor - The Slate editor instance.
   * @returns {boolean} Whether the current impression in the report is generated
   */
  isCurrentImpressionGenerated: (editor: Editor) => boolean;
  /**
   * The state of the impression generation button determined by the generateImpression function call.
   */
  generateImpressionState: GenerateImpressionState;
  /**
   * If impression generator is disabled for the case or user settings
   */
  impressionGeneratorNotAllowed: boolean;
  /**
   * Resets the impression generation state to 'initial' when new template is loaded
   */
  resetGenerateImpressionState: () => void;
  /**
   * Determines if the given impression content is empty.
   * @param {SlateContent} content - The impression content to check.
   * @returns {boolean} True if the content is empty.
   */
  isImpressionContentEmpty: (content: SlateContent) => boolean;
  /**
   * Determines if the user has navigated to the impression section for the first time
   */
  isFirstNavigationToImpressionSection: boolean;
  /**
   * Sets the flag to determine if the user has navigated to the impression section for the first time
   * @param {boolean} isFirstNavigationToImpressionSection - The flag to set
   * @returns {void}
   */
  setIsFirstNavigationToImpressionSection: Dispatch<SetStateAction<boolean>>;
};

export const ImpressionGeneratorContext: React.Context<
  UseImpressionGeneratorReturn | null | undefined
> = createContext<UseImpressionGeneratorReturn | null | undefined>(undefined);

export const ImpressionGeneratorProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement | null => {
  const headingKeywords = useHeadingKeywords();
  const { data } = useCurrentUser();
  const impressionGeneratorSettings = data?.me?.reporterSettings?.impressionGenerator;
  const [isAutoImpressionsEnabled] = useFeatureFlagEnabled(FF.REPORTER_AUTO_IMPRESSIONS);
  const { currentCase } = useCurrentCaseContext();
  const { enqueueToast } = useToasterDispatch();
  const { isAddendumStatus } = useReportLockedState();
  const [generateImpressionState, setGenerateImpressionState] =
    useState<GenerateImpressionState>('initial');
  const previousReportContent = useRef(null);
  const [isFirstNavigationToImpressionSection, setIsFirstNavigationToImpressionSection] =
    useState(true);

  const [prevGeneratedImpression, setPrevGeneratedImpression] = useRecoilState(
    prevGeneratedImpressionState
  );
  const [prevReportSections, setPrevReportSections] = useRecoilState(prevReportSectionsState);
  const [generateImpression, { loading }] = useMutation<
    GenerateImpressionMutation,
    GenerateImpressionMutationVariables
  >(GENERATE_IMPRESSION);
  const [{ editor }] = useSlateSingletonContext();

  const impressionGeneratorNotAllowed =
    isAddendumStatus || !isAutoImpressionsEnabled || !impressionGeneratorSettings?.enabled;

  useEffect(() => {
    if (loading) {
      setGenerateImpressionState('loading');
    }
  }, [loading]);

  const getAnyImpressionHeadingNode = useCallback(
    (editor: Editor) => {
      return (
        getHeadingNodeWithSynonym(editor, headingKeywords.impressions, HeadingLevel.H1) ??
        getHeadingNodeWithSynonym(editor, headingKeywords.impressions, HeadingLevel.H2)
      );
    },
    [headingKeywords.impressions]
  );

  const getImpressionContent = useCallback(
    (editor: Editor): ImpressionContent | null | undefined => {
      const impressionHeadingNode = getAnyImpressionHeadingNode(editor);

      if (impressionHeadingNode == null) {
        return null;
      }

      const headingSectionRange = getHeadingSectionEdgesByNode(editor, impressionHeadingNode);
      if (headingSectionRange == null) {
        return null;
      }

      return {
        content: Editor.fragment(editor, {
          anchor: Editor.start(editor, impressionHeadingNode[1]),
          focus: headingSectionRange.focus,
        }),
        range: headingSectionRange,
        rangeWithHeading: {
          anchor: Editor.start(editor, impressionHeadingNode[1]),
          focus: headingSectionRange.focus,
        },
      };
    },
    [getAnyImpressionHeadingNode]
  );

  const getNonImpressionContent = useCallback(
    (editor: Editor): SlateContent => {
      const impressionHeadingNode = getAnyImpressionHeadingNode(editor);

      if (impressionHeadingNode == null) {
        return editor.children;
      }

      const headingSectionRange = getHeadingSectionEdgesByNode(editor, impressionHeadingNode);
      if (headingSectionRange == null) {
        return editor.children;
      }

      let preImpressionContent: Array<Descendant> = [];
      let postImpressionContent: Array<Descendant> = [];

      const pointBefore = Editor.before(editor, impressionHeadingNode[1]);
      if (pointBefore != null) {
        preImpressionContent = Editor.fragment(editor, {
          anchor: Editor.start(editor, []),
          focus: pointBefore,
        });
      }

      const pointAfter = Editor.after(editor, headingSectionRange.focus);
      if (pointAfter != null) {
        postImpressionContent = Editor.fragment(editor, {
          anchor: pointAfter,
          focus: Editor.end(editor, []),
        });
      }

      return preImpressionContent.concat(postImpressionContent);
    },
    [getAnyImpressionHeadingNode]
  );

  useEffect(() => {
    if (generateImpressionState !== 'visited' || editor == null || editor.children == null) return;
    const reportSections = convertUnifiedToFields(getNonImpressionContent(editor));

    if (!equals(prevReportSections, reportSections)) {
      logger.warn(
        `[useImpressionsGenerator] Report content has been edited and generated impression is now stale`,
        {
          content: reportSections,
        }
      );

      setGenerateImpressionState('stale');
    }
  }, [
    editor,
    editor?.children,
    generateImpressionState,
    getNonImpressionContent,
    prevReportSections,
  ]);

  const generateImpressionInternal = useCallback(
    async (editor: Editor): Promise<SlateContent | null | undefined> => {
      try {
        const impressionHeadingNode = getAnyImpressionHeadingNode(editor);
        if (impressionHeadingNode) {
          const target = ReactEditor.toDOMNode(editor, impressionHeadingNode[0]);
          scrollIntoViewIfNeeded(target, {
            behavior: 'smooth',
            scrollMode: 'if-needed',
            block: 'center',
          });
        }
      } catch (e: any) {
        // no-op, this can happen if we run Slate headlessly since there will be no
        // associated DOM
      }

      if (isAddendumStatus) {
        logger.warn(
          `[useImpressionsGenerator] Impression generation is not available for addendums`
        );
        enqueueToast('Impression generation is not available for addendums', { severity: 'error' });
        return null;
      }

      const currentCaseID = currentCase?.smid;

      if (!isAutoImpressionsEnabled) {
        logger.warn(
          `[useImpressionsGenerator] Impression could not be generated because impression generation is not available for this account`
        );
        return null;
      }

      if (impressionGeneratorSettings?.enabled !== true) {
        logger.warn(
          `[useImpressionsGenerator] Impression could not be generated because impression generation is not enabled`
        );
        return null;
      }

      const reportSections = convertUnifiedToFields(getNonImpressionContent(editor));
      setPrevReportSections(reportSections);

      if (currentCaseID == null || reportSections.length === 0) {
        logger.error(
          `[useImpressionsGenerator] Impression could not be generated because caseSmid or reportSections is null`,
          {
            worklistID: currentCaseID,
            reportSections,
            editor: partialEditor(editor),
          }
        );
        setGenerateImpressionState('error');
        return null;
      }

      try {
        const impressionData = await generateImpression({
          variables: {
            input: { caseSmid: currentCaseID, reportSections },
          },
        });

        logger.info(`[useImpressionsGenerator] Impression generated for case ${currentCaseID}`, {
          content: reportSections,
          impression: impressionData,
          worklistID: currentCaseID,
          editor: partialEditor(editor),
          state: generateImpressionState,
        });

        const impressionContent = annotateTextWithSource(
          impressionData?.data?.generateImpression?.impression,
          slateSource.ai.impressionGenerator
        );
        // Wrap list items in the impression content with bookmarks.
        return wrapListParagraphs(impressionContent);
      } catch (e: any) {
        logger.error('[useImpressionsGenerator] Error generating impression', e, {
          worklistID: currentCaseID,
          reportSections: reportSections,
          editor: partialEditor(editor),
          state: generateImpressionState,
        });
        setGenerateImpressionState('error');
        throw e;
      }
    },
    [
      currentCase?.smid,
      enqueueToast,
      generateImpression,
      generateImpressionState,
      getAnyImpressionHeadingNode,
      getNonImpressionContent,
      impressionGeneratorSettings?.enabled,
      isAddendumStatus,
      isAutoImpressionsEnabled,
      setPrevReportSections,
    ]
  );

  const stitchImpression = useCallback(
    (
      editor: Editor,
      content: SlateContent,
      isAutoGenerated = false
    ): ImpressionContent | null | undefined => {
      if (
        previousReportContent.current != null &&
        previousReportContent.current !== slateContentToString(editor.children) &&
        isAutoGenerated === true
      ) {
        logger.info(
          '[useImpressionsGenerator] Generated impression has been discarded due to the user continuing to edit the report',
          {
            state: generateImpressionState,
            previousContent: previousReportContent,
            content: editor.children,
            isAutoGenerated,
          }
        );
        return null;
      }
      const originalSelection = editor.selection;

      const impressionHeadingNode = getHeadingNodeWithSynonym(editor, headingKeywords.impressions);

      let insertionPoint = null;

      // Temporarily select the start of the editor to avoid issues with selection
      Transforms.select(editor, Editor.start(editor, []));

      // If there is no impression heading, we will need to insert a heading and its necessary
      // inline paragraph and paragraph nodes
      if (impressionHeadingNode == null) {
        logger.info(
          `[useImpressionsGenerator] Inserting impression heading because it does not exist`,
          {
            content: editor.children,
            state: generateImpressionState,
          }
        );

        Transforms.insertNodes(
          editor,
          [
            insertHeading(HeadingLevel.H1, 'Impression:'),
            createInlineParagraph(),
            createParagraph(),
          ],
          {
            at: Editor.end(editor, []),
          }
        );
        insertionPoint = Editor.end(editor, []);
      } else {
        // An impression section exists.
        const headingEdges = getHeadingSectionEdgesByNode(editor, impressionHeadingNode);
        if (!headingEdges) {
          insertionPoint = Editor.end(editor, []);
        } else {
          // First try to replace a previously-generated impression
          // Look for top-level nodes that have generated impression content
          const generatedImpressionRootNodes = Array.from(
            Editor.nodes(editor, {
              at: headingEdges,
              match: (node) => {
                if (!Editor.isBlock(editor, node)) return false;
                const texts = Node.texts(node);
                return Array.from(texts).some(
                  ([textNode]) =>
                    textNode.text !== '' && textNode.source === slateSource.ai.impressionGenerator
                );
              },
              mode: 'highest', // ensures we only get top-level blocks
            })
          );

          if (generatedImpressionRootNodes.length) {
            // Set the insertion point at the start of the first node
            insertionPoint = generatedImpressionRootNodes[0][1];
            const lastNodePath =
              generatedImpressionRootNodes[generatedImpressionRootNodes.length - 1][1];

            // Remove these nodes
            Transforms.removeNodes(editor, {
              at: Editor.range(editor, insertionPoint, lastNodePath),
            });
          } else {
            // There is not previously-generated impression content.
            // Try to find an inlineBookmark in the section
            const maybeInlineBookmarks = Editor.nodes(editor, {
              at: headingEdges,
              match: (n) => Element.isElement(n) && n.type === 'inlineBookmark',
            });
            const bookmarkEntry = maybeInlineBookmarks.next().value;

            if (bookmarkEntry) {
              // Clear only the contents of the existing bookmark
              const [, bookmarkPath] = bookmarkEntry;
              // Ensure the insertion point is at top-level, to replace the parent where the bookmark exists
              insertionPoint = [bookmarkPath[0]];
              Transforms.removeNodes(editor, { at: bookmarkPath.slice(0, 1) });
            } else {
              // Place the insertion point after the first inline paragraph.
              insertionPoint = [headingEdges.anchor.path[0] + 1];
              // insertionPoint = Editor.after(editor, firstInlineParagraph[1]);
            } // no existing bookmark
          } // no previously-generated impression content
        } // heading edges available
      } // existing impression heading

      if (insertionPoint != null) {
        Transforms.insertNodes(editor, content, { at: insertionPoint });

        logger.info(
          `[useImpressionsGenerator] Generated impression has been stitched into editor`,
          {
            content: editor.children,
            impression: content,
            state: generateImpressionState,
          }
        );

        const impressionContent = getImpressionContent(editor);
        if (impressionContent == null) {
          return null;
        }

        if (impressionContent.range != null) {
          Transforms.select(editor, Editor.end(editor, impressionContent.range));
        }

        return impressionContent;
      } else {
        logger.warn(
          `[useImpressionsGenerator] Impression could not be stitched because insertion point is null`,
          {
            content: editor.children,
            impression: content,
            state: generateImpressionState,
          }
        );

        if (originalSelection != null) {
          Transforms.select(editor, originalSelection);
        }

        enqueueToast('Impression generation failed. Please try again later.', {
          severity: 'error',
        });

        return null;
      }
    },
    [enqueueToast, generateImpressionState, getImpressionContent, headingKeywords.impressions]
  );

  const generateAndStitchImpression = useCallback(
    async (
      editor: Editor,
      isAutoGenerated = false
    ): Promise<ImpressionContent | null | undefined> => {
      if (isAddendumStatus) {
        logger.warn(
          `[useImpressionsGenerator] Impression generation is not available for addendums`
        );
        enqueueToast('Impression generation is not available for addendums', { severity: 'error' });
        return null;
      }

      if (!isAutoImpressionsEnabled) {
        logger.warn(
          `[useImpressionsGenerator] Impression could not be generated because impression generation is not available for this account`
        );
        return null;
      }

      if (impressionGeneratorSettings?.enabled !== true) {
        logger.warn(
          `[useImpressionsGenerator] Impression could not be generated because impression generation is not enabled`
        );
        return null;
      }

      if (isAutoGenerated === true) {
        // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
        if (!impressionGeneratorSettings?.modalities?.includes(currentCase?.modality)) {
          logger.warn(
            `[useImpressionsGenerator] Impression could not be automatically generated because impression generation is not enabled for ${currentCase?.modality ?? 'null'} modality`,
            {
              modality: currentCase?.modality,
              enabledModalities: impressionGeneratorSettings?.modalities,
              case: currentCase?.smid,
            }
          );
          return null;
        }

        // @ts-expect-error [EN-10263] - TS2322 - Type 'string' is not assignable to type 'null'.
        previousReportContent.current = slateContentToString(editor.children);
      } else {
        previousReportContent.current = null;
      }

      if (generateImpressionState === 'visited') {
        logger.info(
          `[useImpressionsGenerator] Replacing existing generated impression without changes to the report`,
          {
            content: editor.children,
            state: generateImpressionState,
            impression: prevGeneratedImpression,
          }
        );
      } else if (generateImpressionState === 'stale') {
        logger.info(
          `[useImpressionsGenerator] Replacing existing generated impression after changes to the report`,
          {
            content: editor.children,
            state: generateImpressionState,
            impression: prevGeneratedImpression,
          }
        );
      } else if (generateImpressionState === 'initial') {
        logger.info(`[useImpressionsGenerator] Generating impression for the first time`, {
          content: editor.children,
          state: generateImpressionState,
        });
      }

      let stitchedImpression = null;
      try {
        const impression = await generateImpressionInternal(editor);
        if (impression == null) {
          return null;
        }

        stitchedImpression = stitchImpression(editor, impression, isAutoGenerated);
        if (stitchedImpression != null && stitchedImpression.content != null) {
          setPrevGeneratedImpression(slateContentToString(stitchedImpression.content));
        }
        setGenerateImpressionState('visited');
      } catch (e: any) {
        const detail = e.message || 'Please try again later';
        enqueueToast(`An error occurred when processing your request: ${detail}`, {
          severity: 'error',
        });
      }

      return stitchedImpression;
    },
    [
      isAddendumStatus,
      isAutoImpressionsEnabled,
      impressionGeneratorSettings?.enabled,
      impressionGeneratorSettings?.modalities,
      generateImpressionState,
      enqueueToast,
      currentCase?.modality,
      currentCase?.smid,
      prevGeneratedImpression,
      generateImpressionInternal,
      stitchImpression,
      setPrevGeneratedImpression,
    ]
  );

  /**
   * Returns whether the current impression has been generated in the editor by comparing the
   * content of the most recently generated impression with the content of the current impression
   */
  const isCurrentImpressionGenerated = useCallback(
    (editor: Editor): boolean => {
      if (prevGeneratedImpression == null) {
        return false;
      }

      const impressionContent = getImpressionContent(editor);
      if (impressionContent == null || impressionContent.content == null) {
        return false;
      }

      return slateContentToString(impressionContent.content) === prevGeneratedImpression;
    },
    [getImpressionContent, prevGeneratedImpression]
  );

  const resetGenerateImpressionState = useCallback(() => {
    setGenerateImpressionState('initial');
  }, [setGenerateImpressionState]);

  const isSelectionInImpressionSection = useCallback(
    (editor: Editor, newSelection?: Range): boolean => {
      const impressionRange = getImpressionContent(editor)?.rangeWithHeading;
      const selection = newSelection ?? editor.selection;

      return (
        impressionRange != null &&
        selection != null &&
        Range.includes(impressionRange, selection.anchor) &&
        Range.includes(impressionRange, selection.focus)
      );
    },
    [getImpressionContent]
  );

  const isImpressionContentEmpty = useCallback(
    (impressionContent: ImpressionContent | null): boolean => {
      const content = impressionContent?.content;
      // This really implies that it is missing - not that it is empty
      if (!content || content.length === 0 || headingKeywords.impressions == null) {
        return false;
      }
      const lowerKeywords = headingKeywords.impressions.map((keyword) => keyword.toLowerCase());
      const transformedHeading = content[0].children[0].text.trim().toLowerCase().replace(':', '');
      if (content[0].type === 'heading' && !lowerKeywords.includes(transformedHeading)) {
        return false;
      }

      // @ts-expect-error [EN-10263] - TS7006 - Parameter 'node' implicitly has an 'any' type.
      const isEmptyTextNode = (node) => {
        if (Text.isText(node)) {
          return node.text.trim() === '';
        }
        if ('children' in node) {
          return node.children.every(isEmptyTextNode);
        }
        return true;
      };
      // @ts-expect-error [EN-10263] - TS7006 - Parameter 'node' implicitly has an 'any' type.
      const isContentEmpty = content.slice(1).every((node) => node.children.every(isEmptyTextNode));

      return isContentEmpty;
    },
    [headingKeywords.impressions]
  );

  const impressionGeneratorBag = useMemo(
    () => ({
      isSelectionInImpressionSection,
      getImpressionContent,
      getNonImpressionContent,
      generateImpression: generateImpressionInternal,
      stitchImpression,
      generateAndStitchImpression,
      isCurrentImpressionGenerated,
      generateImpressionState,
      impressionGeneratorNotAllowed,
      resetGenerateImpressionState,
      isImpressionContentEmpty,
      isFirstNavigationToImpressionSection,
      setIsFirstNavigationToImpressionSection,
    }),
    [
      isSelectionInImpressionSection,
      getImpressionContent,
      getNonImpressionContent,
      generateImpressionInternal,
      stitchImpression,
      generateAndStitchImpression,
      isCurrentImpressionGenerated,
      generateImpressionState,
      impressionGeneratorNotAllowed,
      resetGenerateImpressionState,
      isImpressionContentEmpty,
      isFirstNavigationToImpressionSection,
    ]
  );

  return (
    <ImpressionGeneratorContext.Provider value={impressionGeneratorBag}>
      {children}
    </ImpressionGeneratorContext.Provider>
  );
};

/**
 * Custom hook to generate and manage impression content within a Slate editor.
 * This hook provides functionality to retrieve impression content, generate new impressions,
 * and stitch them into the editor.
 *
 * @returns {UseImpressionGeneratorReturn} An object containing methods to:
 *  - getImpressionContent: Retrieves the impression content and its range from the editor.
 *  - getNonImpressionContent: Retrieves the content of the editor excluding the impression section.
 *  - generateImpression: Generates an impression for the current case and returns the impression content.
 *  - stitchImpression: Stitches given impression content into the editor.
 *  - generateAndStitchImpression: Generates an impression and stitches it into the editor.
 *
 **/
export const useImpressionGenerator = (): UseImpressionGeneratorReturn => {
  const impressionGenerator = useContext(ImpressionGeneratorContext);

  if (!impressionGenerator) {
    throw new Error(
      '[useImpressionGenerator] Hook must be used within a ImpressionGeneratorProvider.'
    );
  }

  return impressionGenerator;
};

// Helper function to wrap list items in inlineBookmarks in the impression content.
export function wrapListParagraphs(nodes: Node[]) {
  return nodes.map((node: Node): Node => {
    if (!Element.isElement(node)) {
      // text nodes pass through unchanged
      return node;
    }
    if (node.type === 'paragraph' && node.variant === 'li') {
      return {
        ...node,
        // wrap all original children in an inlineBookmark
        children: [
          {
            type: 'inlineBookmark',
            // carry over any additional element-level props if needed
            // e.g. id, data-attrs, etc.
            children: node.children,
          },
        ],
      };
    }
    // otherwise recurse into its children
    return {
      ...node,
      children: wrapListParagraphs(node.children),
    };
  });
}
